export type Series = {
  episodeCount: number;
  masterId: string;
  seasonCount: number;
  titleName: string;
};

export type Season = {
  episodeCount: number;
  sequenceNumber: number;
  titleName: string;
};

export type Episode = {
  masterId: string;
  sequenceNumber: number;
  titleName: string;
};

type PlaybackOption = {
  availableFrom: string;
  availableTo: string;
  endTime: string;
  id: string;
  isDefault?: boolean;
  location: string;
  platformTitleId: string;
  providerIdentifier: string;
  providerTitleId: string;
  serviceType: string;
  sourceId: string;
  startTime: string;
};

export type DetailedPlaybackOption = PlaybackOption & {
  data?: {
    channelId: string;
    channelName: string;
    supportsCatchup: boolean;
    supportsCloudRecording: boolean;
    supportsLocalRecording: boolean;
  };
  providerType?: string;
};

type MultiLanguageText = Partial<Record<'da' | 'en' | 'no', string | null>>;
type MultiLanguageList = Partial<Record<'da' | 'en' | 'no', string[] | null>>;

export type PlaybackOptionDetailed = {
  ageRating?: number | null;
  allowDownload?: boolean | null;
  audioLanguages?: Record<string, unknown>[] | null;
  availableFrom?: string | null;
  availableTo?: string | null;
  cast?: Cast[] | null;
  casting?: { airPlay: boolean; chromecast: boolean } | null;
  channelId?: string | null;
  channelName?: string | null;
  country?: string | null;
  descriptionLong?: MultiLanguageText | null;
  descriptionShort?: MultiLanguageText | null;
  distributor?: string | null;
  duration?: number | null;
  endTime?: string | null;
  episode?: Episode | null;
  flags?: Flags | null;
  hasTrailer?: boolean | null;
  id?: string | null;
  lifetimeId?: string | null;
  links?: Record<string, unknown> | null;
  live?: boolean | null;
  media?: Record<string, unknown>[] | null;
  mergeReason?: string | null;
  originalTitle?: string | null;
  platformTitleId?: string | null;
  price?: { amount?: number | null; currency?: string | null }[] | null;
  productionYear?: number | null;
  providerIdentifier?: string | null;
  providerName?: string | null;
  providerTitleId?: string | null;
  providerType?: string | null;
  scoreRating?: ScoreRating | null;
  season?: Season | null;
  series?: Series | null;
  seriesId?: string | null;
  serviceType?: string | null;
  sourceDuration?: number | null;
  sourceId?: string | null;
  startTime?: string | null;
  subtitleLanguages?: Record<string, unknown>[] | null;
  supportsCatchup?: boolean | null;
  tags?: MultiLanguageList | null;
  titleType?: TitleType | null;
};

const _CastRoles = ['CastMember', 'Director', 'Actor', 'Writer', 'Producer'] as const;
export type CastRole = (typeof _CastRoles)[number];

export type Cast = {
  name?: string;
  role?: CastRole | string;
};

const _ScoreRatingProviders = ['imdb', 'dagbladet', 'vg', 'rottenTomatoes'] as const;
export type ScoreRatingProvider = (typeof _ScoreRatingProviders)[number];

export type ScoreRating = Record<ScoreRatingProvider, null | number>;

export type Flags = {
  isRepeatingSeries?: boolean;
  norwegianMediaAuthorityId?: string;
};

export const TitleTypes = ['movie', 'series', 'episode', 'program'] as const;
export type TitleType = (typeof TitleTypes)[number];

export type TitleBase = {
  descriptionLong: string;
  episode?: Episode;
  flags: Flags;
  genres: string[];
  masterId: string;
  originalTitle: string;
  productionYear?: number;
  providers: string[];
  season?: Season;
  series?: Series;
  slug: string;
  titleName: string;
  type: TitleType;
};

export type SimpleTitle = TitleBase;

export type DetailedTitle = TitleBase & {
  ageRating: number;
  cast: Cast[];
  country: string;
  descriptionShort: string;
  duration: number;
  playbackOptions: DetailedPlaybackOption[];
  price: number;
  scoreRating?: ScoreRating;
  tags?: string[];
};

export type Title = DetailedTitle | SimpleTitle;

export type MasterIdTitleContext = {
  masterId: string;
  platformTitleId?: string;
  playbackOptionId?: string;
  providerIdentifier?: string;
};

type PlatformIdTitleContext = {
  masterId?: string;
  platformTitleId: string;
  playbackOptionId?: string;
  providerIdentifier?: string;
};

export type TTitleContext = MasterIdTitleContext | PlatformIdTitleContext;
